import React from 'react';
import { motion } from 'framer-motion';
import { ArrowsLeftRight, BookOpen, ChatCircleDots, CursorClick, Eye, Flag, Users } from '@phosphor-icons/react';
import { Overlay } from './Overlay';
import { signals, signalsClosing } from '../../data/signals';
import type { SignalIcon } from '../../data/signals';
import type { Format } from '../../types/snak';
interface SignalsOverlayProps {
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
}
export function SignalsOverlay({
  format,
  onFormatChange,
  onClose
}: SignalsOverlayProps) {
  const compact = format === 'mobile';
  return <Overlay eyebrow="What we learn" format={format} onFormatChange={onFormatChange} onClose={onClose} data-id="element-230" data-mp="365hffi5odr9">
      <div className={compact ? 'px-6 pb-16 pt-10' : 'mx-auto max-w-[900px] px-6 pb-24 pt-16 lg:pt-24'} data-id="element-231" data-mp="3ua3xsqq0wq1u">
        <h1 className={['max-w-[20ch] font-heading leading-[1] tracking-[-0.03em] text-background', compact ? 'text-[31px]' : 'text-[42px] sm:text-[52px]'].join(' ')} data-id="element-232" data-mp="3lb0d5ib7k2l8">
          Every interaction creates a signal.
        </h1>

        <div className={['grid', compact ? 'mt-10 gap-7' : 'mt-16 gap-x-12 gap-y-9 sm:grid-cols-2'].join(' ')} data-id="element-233" data-mp="2gz3rsemoyvvo">
          {signals.map((signal, i) => <motion.div key={signal.id} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + i * 0.06,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-start gap-4" data-id="element-234" data-mp="39mzo3i8cmlrx" data-mpi={i}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-background ring-1 ring-[rgba(1,51,43,0.1)]" data-id="element-235" data-mp="34nek2tebnn8c">
                <SignalGlyph icon={signal.id} data-id="element-236" data-mp="1p9s8b0b7m44o" />
              </span>
              <span className="pt-0.5" data-id="element-237" data-mp="1takipwvtif2j">
                <span className={['block font-heading leading-tight text-background', compact ? 'text-[15px]' : 'text-[16px]'].join(' ')} data-id="element-238" data-mp="3vvoieatpfvor">
                  {signal.label}
                </span>
                <span className="mt-1.5 block max-w-[34ch] font-body text-[12.5px] leading-relaxed text-[rgba(1,51,43,0.55)]" data-id="element-239" data-mp="3m3ikylkoom26">
                  {signal.detail}
                </span>
              </span>
            </motion.div>)}
        </div>

        <p className={['border-t border-[rgba(1,51,43,0.14)] font-heading leading-[1.18] tracking-[-0.02em] text-background', compact ? 'mt-12 max-w-[26ch] pt-8 text-[20px]' : 'mt-20 max-w-[38ch] pt-10 text-[26px]'].join(' ')} data-id="element-240" data-mp="385aqckb4dwu6">
          {signalsClosing}
        </p>
      </div>
    </Overlay>;
}
function SignalGlyph({
  icon
}: {
  icon: SignalIcon;
}) {
  const size = 19;
  if (icon === 'role') return <Users size={size} aria-hidden="true" data-id="element-241" data-mp="14yd2497g7hew" />;
  if (icon === 'response') return <ChatCircleDots size={size} aria-hidden="true" data-id="element-242" data-mp="18yx0fg3rcr2m" />;
  if (icon === 'association') return <ArrowsLeftRight size={size} aria-hidden="true" data-id="element-243" data-mp="18wsnk8nvshq7" />;
  if (icon === 'explored') return <Eye size={size} aria-hidden="true" data-id="element-244" data-mp="1dryi1vvcn4pw" />;
  if (icon === 'completion') return <Flag size={size} aria-hidden="true" data-id="element-245" data-mp="1innc5jolntet" />;
  if (icon === 'cta') return <CursorClick size={size} aria-hidden="true" data-id="element-246" data-mp="lz7gr6yt6pts" />;
  return <BookOpen size={size} aria-hidden="true" data-id="element-247" data-mp="m0set6kc0qwe" />;
}